import React, { useState, useEffect } from 'react';
import { ECMR } from '../types/eCMR';
import { getStandardCodes } from '../services/schemaValidator';

interface ECMRFormProps {
  initialData: ECMR;
  onSubmit: (data: ECMR) => void;
  onCancel: () => void;
}

const ECMRForm: React.FC<ECMRFormProps> = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<ECMR>(initialData);
  const [countryCodes, setCountryCodes] = useState<{ code: string, description: string }[]>([]);
  const [transportModeCodes, setTransportModeCodes] = useState<{ code: string, description: string }[]>([]);
  const [packageTypeCodes, setPackageTypeCodes] = useState<{ code: string, description: string }[]>([]);
  const [deliveryTermsCodes, setDeliveryTermsCodes] = useState<{ code: string, description: string }[]>([]);
  
  useEffect(() => {
    // Cargar los códigos estándar
    setCountryCodes(getStandardCodes('CountryCode'));
    setTransportModeCodes(getStandardCodes('TransportModeCode'));
    setPackageTypeCodes(getStandardCodes('PackageTypeCode'));
    setDeliveryTermsCodes(getStandardCodes('DeliveryTermsCode'));
  }, []);
  
  const handleChange = (section: keyof ECMR, field: string, value: any) => {
    if (section === 'ExchangedDocument') {
      setFormData({
        ...formData,
        ExchangedDocument: {
          ...formData.ExchangedDocument,
          [field]: value
        }
      });
    } else if (section === 'SpecifiedSupplyChainConsignment') {
      setFormData({
        ...formData,
        SpecifiedSupplyChainConsignment: {
          ...formData.SpecifiedSupplyChainConsignment,
          [field]: value
        }
      });
    }
  };
  
  const handlePartyAddressChange = (partyType: 'ConsignorTradeParty' | 'ConsigneeTradeParty' | 'CarrierTradeParty', field: string, value: any) => {
    setFormData({
      ...formData,
      SpecifiedSupplyChainConsignment: {
        ...formData.SpecifiedSupplyChainConsignment,
        [partyType]: {
          ...formData.SpecifiedSupplyChainConsignment[partyType],
          PostalTradeAddress: {
            ...formData.SpecifiedSupplyChainConsignment[partyType]?.PostalTradeAddress,
            [field]: value
          }
        }
      }
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Función para renderizar selector de códigos estándar
  const renderCodeSelector = (
    label: string, 
    value: string | undefined, 
    onChange: (value: string) => void, 
    options: { code: string, description: string }[],
    required: boolean = false
  ) => {
    return (
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required={required}
        >
          <option value="">Seleccionar...</option>
          {options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.code} - {option.description}
            </option>
          ))}
        </select>
      </div>
    );
  };
  
  return (
    <div className="container mx-auto p-4">
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded p-6">
        {/* Información básica del documento */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información del Documento</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="documentId">
                ID del Documento <span className="text-red-500">*</span>
              </label>
              <input
                id="documentId"
                type="text"
                value={formData.ExchangedDocument?.ID || ''}
                onChange={(e) => handleChange('ExchangedDocument', 'ID', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="issueLocation">
                Lugar de Emisión <span className="text-red-500">*</span>
              </label>
              <input
                id="issueLocation"
                type="text"
                value={formData.ExchangedDocument?.IssueLogisticsLocation?.Name || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ExchangedDocument: {
                      ...formData.ExchangedDocument,
                      IssueLogisticsLocation: {
                        ...formData.ExchangedDocument?.IssueLogisticsLocation,
                        Name: e.target.value
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="remarks">
                Observaciones
              </label>
              <input
                id="remarks"
                type="text"
                value={formData.ExchangedDocument?.Remarks?.[0] || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ExchangedDocument: {
                      ...formData.ExchangedDocument,
                      Remarks: [e.target.value]
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>
        </div>
        
        {/* Información del Remitente */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Remitente</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsignorTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsignorTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorPostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorPostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el remitente */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('ConsignorTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información del Destinatario */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Destinatario</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsigneeTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsigneeTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Dirección del destinatario (similar al remitente) */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneePostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneePostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el destinatario */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('ConsigneeTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información del Transportista */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Transportista</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.CarrierTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      CarrierTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.CarrierTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      CarrierTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierPostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierPostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el transportista */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('CarrierTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información de Mercancía y Embalaje */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Mercancía y Embalaje</h2>
          
          {/* Selectores para tipos de embalaje */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Tipo de Embalaje
            </label>
            <select
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              onChange={(e) => {
                // Lógica para añadir un nuevo tipo de embalaje a los artículos
                // Esta es una versión simplificada - se necesitaría un manejo más complejo para múltiples artículos
                const packageType = e.target.value;
                if (!packageType) return;
                
                // Crear un nuevo artículo si no existe
                if (!formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem) {
                  const newItem = {
                    SequenceNumeric: 1,
                    TransportLogisticsPackage: [
                      {
                        TypeCode: packageType,
                        ItemQuantity: 1,
                        Description: 'Nuevo artículo'
                      }
                    ]
                  };
                  
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      IncludedSupplyChainConsignmentItem: [newItem]
                    }
                  });
                }
              }}
            >
              <option value="">Seleccionar...</option>
              {packageTypeCodes.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.code} - {option.description}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Términos de Entrega */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Términos de Entrega</h2>
          
          {/* Selector para términos de entrega (Incoterms) */}
          {renderCodeSelector(
            'Términos de Entrega (Incoterms)',
            formData.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms?.DeliveryTypeCode,
            (value) => {
              setFormData({
                ...formData,
                SpecifiedSupplyChainConsignment: {
                  ...formData.SpecifiedSupplyChainConsignment,
                  SpecifiedTradeDeliveryTerms: {
                    ...formData.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms,
                    DeliveryTypeCode: value
                  }
                }
              });
            },
            deliveryTermsCodes
          )}
          
          {/* Transbordo permitido */}
          <div className="mb-4">
            <label className="flex items-center text-gray-700 text-sm font-bold">
              <input
                type="checkbox"
                checked={formData.SpecifiedSupplyChainConsignment.TransshipmentPermissionIndicator || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      TransshipmentPermissionIndicator: e.target.checked
                    }
                  });
                }}
                className="mr-2"
              />
              Transbordo permitido
            </label>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Guardar
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default ECMRForm;